import { injectable } from 'inversify';
import { flatten } from 'lodash';

import { FormatService } from '@vk-hr-tek/core/format';
import { Calendar } from '@vk-hr-tek/core/calendar';

import { BellNotification } from '@app/gen/bell';

import { Notification, NotificationGroup } from '../types';
import { NOTIFIABLE_NICK_ICONS } from '../constants';

@injectable()
export class NotificationsMapper {
  constructor(private format: FormatService, private calendar: Calendar) {}

  private formatGroupLabel(date: Date): string {
    return new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'long',
    }).format(date);
  }

  processEntity(notification: BellNotification): Notification {
    return {
      id: notification.id,
      notifiableName: notification.notifiable_name,
      notifiableNick: notification.notifiable_nick,
      actionName: notification.action_name,
      actionNick: notification.action_nick,
      message: notification.message,
      createdAt: notification.created_at,
      isRead: !!notification.read_at,
      icon: NOTIFIABLE_NICK_ICONS[notification.notifiable_nick],
      payload: notification.payload,
    };
  }

  processEntityListWithDateGroups(
    notifications: BellNotification[],
  ): NotificationGroup[] {
    const todayDate = new Date();
    const today = this.formatGroupLabel(todayDate);
    const yesterday = this.formatGroupLabel(this.calendar.sub(todayDate, 1));

    const groupedNotifications: Record<string, NotificationGroup[]> = {
      today: [{ label: `Сегодня, ${today}`, type: 'group' }],
      yesterday: [{ label: `Вчера, ${yesterday}`, type: 'group' }],
      thisWeek: [{ label: 'На этой неделе', type: 'group' }],
      thisMonth: [{ label: 'В этом месяце', type: 'group' }],
      older: [{ label: 'Более месяца назад', type: 'group' }],
    };

    notifications.forEach((notification) => {
      const mappedNotification = {
        ...this.processEntity(notification),
        type: 'item' as const,
      };
      const { createdAt } = mappedNotification;

      switch (true) {
        case this.calendar.isToday(createdAt):
          return groupedNotifications.today.push({
            ...mappedNotification,
            createdAt: this.format.toTime(createdAt),
          });
        case this.calendar.isYesterday(createdAt):
          return groupedNotifications.yesterday.push({
            ...mappedNotification,
            createdAt: this.format.toTime(createdAt),
          });
        case this.calendar.isThisWeek(createdAt, 1):
          return groupedNotifications.thisWeek.push({
            ...mappedNotification,
            createdAt: this.format.toDateTime(createdAt),
          });
        case this.calendar.isThisMonth(createdAt):
          return groupedNotifications.thisMonth.push({
            ...mappedNotification,
            createdAt: this.format.toDateTime(createdAt),
          });
        default:
          return groupedNotifications.older.push({
            ...mappedNotification,
            createdAt: this.format.toDate(createdAt),
          });
      }
    });

    return flatten(
      Object.values(groupedNotifications).filter(
        (groupArray) => groupArray.length > 1,
      ),
    );
  }
}
